import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { subscriptionBasedGivingContent } from '../content/educational/subscriptionBasedGivingContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 35;
    const footerHeight = 12;

    // Font sizes
    const fonts = {
      title: 22,
      sectionHeading: 14,
      subHeading: 12,
      body: 11,
      footer: 8
    };

    // Colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255]
    };

    // Generate QR Codes
    const qrCodeAcademy = await QRCode.toDataURL('https://causeandimpact.org/academy');
    const qrCodeDonate = await QRCode.toDataURL('https://causeandimpact.org/donate');

    // Header component
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow effect
      doc.setTextColor(...colors.blue);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(...colors.green);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(...colors.white);
      doc.text('Expert Resources for Nonprofit Success', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeAcademy, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    // Footer component
    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.footer);
      doc.setTextColor(...colors.white);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Add Call to Action component
    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 28;
      
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 6, 6, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(...colors.white);
      doc.text('Found this helpful?', pageWidth / 2, yPosition + 18, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Scan to donate to one of our many Causes.', pageWidth / 2, yPosition + 28, { align: 'center' });
      
      const qrX = pageWidth - qrSize - margin - 6;
      const qrY = yPosition + 8;
      
      doc.setFillColor(0, 0, 0, 0.06);
      doc.roundedRect(qrX + 0.8, qrY + 0.8, qrSize, qrSize, 2, 2, 'F');
      doc.setFillColor(0, 0, 0, 0.04);
      doc.roundedRect(qrX + 0.4, qrY + 0.4, qrSize, qrSize, 2, 2, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX, qrY, qrSize, qrSize, 2, 2, 'F');
      
      doc.addImage(qrCodeDonate, 'PNG', qrX, qrY, qrSize, qrSize);
    };

    // Add markdown processing
    const processMarkdown = (text) => {
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      if (text.startsWith("'") || text.startsWith('✓')) {
        return [{
          text: text.trim(),
          bold: false
        }];
      }
      
      while ((match = boldRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < text.length) {
        segments.push({
          text: text.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Process list with proper formatting
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 10;
      const subIndent = 6;
      const lineSpacing = 4;

      // Define consistent sizes
      const SIZES = {
        HEADER: 14,
        SUBHEADER: 12,
        BODY: 11,
        BULLET: 11
      };

      // Font settings
      const fontSettings = {
        header: { size: SIZES.HEADER, style: 'bold', color: colors.green },
        body: { size: SIZES.BODY, style: 'normal', color: colors.gray },
        bullet: { size: SIZES.BULLET, style: 'normal', color: colors.gray }
      };

      // Font state management
      let currentFontState = {
        size: SIZES.BODY,
        style: 'normal',
        color: colors.gray
      };

      const setFontState = (state, force = false) => {
        if (force || 
            state.size !== currentFontState.size || 
            state.style !== currentFontState.style || 
            state.color !== currentFontState.color) {
          currentFontState = { ...state };
          doc.setFont('helvetica', state.style);
          doc.setFontSize(state.size);
          doc.setTextColor(...state.color);
        }
      };

      const saveFontState = () => {
        return {
          size: currentFontState.size,
          style: currentFontState.style,
          color: currentFontState.color
        };
      };

      const restoreFontState = (state) => {
        setFontState(state, true);
      };

      // Page break handler
      const checkPageBreak = (height = 4) => {
        if (currentY + height > pageHeight - footerHeight - 15) {
          const savedState = saveFontState();
          
          doc.addPage();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
          currentY = headerHeight + 12;
          
          restoreFontState(savedState);
          return true;
        }
        return false;
      };

      // Render text with consistent formatting
      const renderTextWithBold = (text, x, y, forcedSize = null) => {
        const segments = processMarkdown(text);
        let currentX = x;
        const currentSize = forcedSize || doc.getFontSize();
        
        segments.forEach(segment => {
          doc.setFontSize(currentSize);
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          doc.setTextColor(...colors.gray);
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        
        doc.setFontSize(currentSize);
      };

      // Add numbered list handling
      const isNumberedListItem = (text) => {
        return /^\d+\)/.test(text.trim());
      };

      // Process each line
      lines.forEach(line => {
        const trimmedLine = line.trim();

        // Handle numbered lists
        if (isNumberedListItem(trimmedLine)) {
          const match = trimmedLine.match(/^(\d+\))\s*(.*)/);
          if (match) {
            const [, number, itemText] = match;
            const indent = bulletIndent + 4;
            
            const numberSize = SIZES.BULLET;
            doc.setFontSize(numberSize);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...colors.gray);
            
            doc.text(number, margin + 4, currentY);
            
            const textWidth = contentWidth - (indent + 12);
            const textLines = doc.splitTextToSize(itemText, textWidth);
            
            doc.setFont('helvetica', 'normal');
            
            textLines.forEach((line, index) => {
              if (index > 0) {
                currentY += lineSpacing;
                if (checkPageBreak(lineSpacing)) {
                  doc.setFontSize(numberSize);
                  doc.setFont('helvetica', 'normal');
                  doc.setTextColor(...colors.gray);
                }
              }
              renderTextWithBold(line, margin + indent + 2, currentY, numberSize);
            });
            
            currentY += lineSpacing + 2;
            return;
          }
        }

        // Handle section headers
        if (trimmedLine.startsWith('###')) {
          checkPageBreak(6);
          setFontState(fontSettings.header);
          doc.text(trimmedLine.replace('###', '').trim(), margin, currentY);
          currentY += 6;
          setFontState(fontSettings.body);
          return;
        }

        // Handle bullet points
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
          const bulletText = trimmedLine.substring(1).trim();
          const indent = trimmedLine.startsWith('   ') ? bulletIndent + subIndent : bulletIndent;
          
          const bulletSize = SIZES.BULLET;
          doc.setFontSize(bulletSize);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...colors.gray);
          
          doc.text('•', margin + indent - 3, currentY);
          
          const textLines = doc.splitTextToSize(bulletText, contentWidth - (indent + 6));
          textLines.forEach((line, index) => {
            if (index > 0) {
              currentY += lineSpacing;
              if (checkPageBreak(lineSpacing)) {
                doc.setFontSize(bulletSize);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...colors.gray);
              }
            }
            renderTextWithBold(line, margin + indent + 3, currentY, bulletSize);
          });
          
          currentY += lineSpacing + 1;
          return;
        }

        // Handle regular text
        if (trimmedLine) {
          checkPageBreak(lineSpacing);
          setFontState(fontSettings.body);
          
          const textLines = doc.splitTextToSize(trimmedLine, contentWidth);
          textLines.forEach(line => {
            renderTextWithBold(line, margin, currentY);
            currentY += lineSpacing;
          });
        } else {
          currentY += lineSpacing - 1;
        }
      });

      return currentY;
    };

    // Content generation
    const addContent = () => {
      let yPosition = headerHeight + 10;
      let pageNumber = 1;

      // Add title
      doc.setFontSize(fonts.title);
      doc.setTextColor(...colors.blue);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 2, { align: 'center' });
      yPosition += (titleLines.length * 8) + 12;

      // Process sections
      content.forEach((section, index) => {
        if (yPosition + 40 > pageHeight - footerHeight - 15) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 10;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.setTextColor(...colors.green);
        doc.text(section.heading, margin, yPosition);
        yPosition += 10;

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);

        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          if (yPosition + 40 > pageHeight - footerHeight - 15) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 10;
          }

          yPosition = processList(paragraph, yPosition);
          yPosition += 4;
        });

        yPosition += 12;
      });

      // Add CTA
      if (yPosition + 45 > pageHeight - footerHeight - 15) {
        doc.addPage();
        pageNumber++;
        addHeader();
        addFooter(pageNumber);
        yPosition = headerHeight + 10;
      }

      yPosition += 6;
      addCallToAction(yPosition);
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateSubscriptionBasedGivingOverview = async () => {
  return generatePDF(
    subscriptionBasedGivingContent.sections,
    subscriptionBasedGivingContent.title,
    subscriptionBasedGivingContent.keywords
  );
}; 